// file: client/src/components/billing/ModuleGate.jsx
/**
 * PHARMAKON — ModuleGate
 *
 * Wraps a page (or any subtree) and renders its children only when the
 * current user's licence has the requested paid module activated.
 *
 * Resolution order for the active modules list (first non-empty wins):
 *   1. user.modules                — top-level flattened array from /auth/me
 *      (see server/src/services/auth.service.js → getMe)
 *   2. user.licence.modules        — BETA mock shape (mock/data/pharmacie.js)
 *   3. /auth/me on mount           — fallback when neither is present on the
 *                                    cached user object (e.g. legacy login
 *                                    before the modules flattening landed)
 *
 * Bypass rules:
 *   • role === "ADMIN_PHARMAKON"   → always renders children
 *   • modules contains "*"         → always renders children (server uses
 *                                    "*" to denote "all modules", see
 *                                    server/src/middleware/licence.js)
 *
 * Otherwise, renders a centered locked-state Card matching the design
 * (Lock icon · titre · sous-titre · 2 boutons "Voir les modules" / "Plus
 * tard"). The component is presentational only — no auto-redirect — the
 * api.client interceptor surfaces a toast on 403 FORBIDDEN with a module
 * code; the empty state here gives the user a clear path to /abonnement.
 *
 * Props:
 *   • moduleCode   string         — required, e.g. "AIDE_DECISION"
 *   • children     ReactNode      — page content to render when allowed
 *   • title?       string         — override locked-state title
 *   • subtitle?    string         — override locked-state subtitle
 *   • priceLabel?  string         — appended to the default subtitle
 *                                   (default: "16 000 DA HT / an")
 *   • onDismiss?   () => void     — when provided, shows "Plus tard" button
 *                                   that calls this (e.g. to close a tab);
 *                                   omit when used at page-level (no-op).
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Icon,
} from "@/components/ui/primitives";
import { useAuthStore } from "@/stores/authStore.js";
import { pharmacyHasModule, userModules } from "@/config/modules.config.js";

const SUBSCRIPTION_ROUTE = "/abonnement";
const ADMIN_BYPASS_ROLE = "ADMIN_PHARMAKON";
const WILDCARD_MODULE = "*";

/** The modules array off a user object ([] when the session does not say). */
const resolveModules = (user) => userModules(user) ?? [];

export default function ModuleGate({
  moduleCode,
  children,
  title,
  subtitle,
  priceLabel = "16 000 DA HT / an",
  onDismiss,
}) {
  const user = useAuthStore((s) => s.user);
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const navigate = useNavigate();

  // Fallback: if the cached user has no modules info anywhere, ask the
  // backend once. Avoids gating users who logged in before the modules
  // flattening landed in /auth/me.
  const triedRefreshRef = React.useRef(false);
  React.useEffect(() => {
    if (!user) return;
    if (triedRefreshRef.current) return;
    const hasAnyModulesField =
      Array.isArray(user.modules) || Array.isArray(user.licence?.modules);
    if (hasAnyModulesField) return;
    triedRefreshRef.current = true;
    refreshUser?.().catch(() => {});
  }, [user, refreshUser]);

  // Bypass: PHARMAKON admin sees everything regardless of pharmacy licence.
  if (user?.role === ADMIN_BYPASS_ROLE) return <>{children}</>;

  const modules = resolveModules(user);

  // Bypass: wildcard "*" (matches server-side req.licence.modules = ["*"]).
  // pharmacyHasModule also alias-resolves (CHATBOT → AIDE_AU_CONSEIL) so a
  // pharmacy entitled to the deprecated module unlocks the canonical one.
  if (modules.includes(WILDCARD_MODULE)) return <>{children}</>;
  if (pharmacyHasModule(modules, moduleCode)) return <>{children}</>;

  // ── Locked state ─────────────────────────────────────────────────────────
  const lockedTitle = title ?? "Module Aide à la Décision non activé";
  const lockedSubtitle =
    subtitle ??
    `Souscrivez au module Aide à la Décision pour activer cette fonctionnalité (${priceLabel}).`;

  return (
    <div className="flex items-center justify-center w-full min-h-[60vh] px-6 py-10">
      <Card
        padding="lg"
        className="max-w-md w-full text-center flex flex-col items-center"
      >
        <div className="w-14 h-14 rounded-full bg-bg-sunken flex items-center justify-center mb-4 text-text-muted">
          <Icon name="lock" size={24} />
        </div>
        <h3 className="text-md font-semibold text-text-strong mb-1">
          {lockedTitle}
        </h3>
        <p className="text-sm text-text-muted mb-5">{lockedSubtitle}</p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <Button
            variant="primary"
            size="md"
            icon="arrow-right"
            onClick={() => navigate(SUBSCRIPTION_ROUTE)}
          >
            Voir les modules
          </Button>
          {onDismiss && (
            <Button variant="secondary" size="md" onClick={onDismiss}>
              Plus tard
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
